/**
 * Unsent log-draft edits kept in this browser (see wizard/draft-sync.ts). Keys carry the signed-in
 * user, so on a shared farm-office tablet one person's half-finished spray record is never offered
 * to, or readable as, someone else's. Everything here is cleared on sign-out.
 */
const PREFIX = 'farmula:log-draft:'

/** What is kept per draft: the last step reached, the values, and whether the server is behind. */
export type LocalDraft = { step: number; values: Record<string, unknown>; dirty: boolean }

export interface DraftStore {
  read(key: string): LocalDraft | null
  write(key: string, draft: LocalDraft): void
  clear(key: string): void
}

/** The store for one user. Storage that is full or blocked degrades to "server copy only". */
export function localDraftStore(userId: string): DraftStore {
  const full = (key: string) => `${PREFIX}${userId}:${key}`
  return {
    read(key) {
      try {
        const raw = localStorage.getItem(full(key))
        return raw ? (JSON.parse(raw) as LocalDraft) : null
      } catch {
        return null
      }
    },
    write(key, draft) {
      try {
        localStorage.setItem(full(key), JSON.stringify(draft))
      } catch {
        // Storage full or blocked: the server copy still works.
      }
    },
    clear(key) {
      try {
        localStorage.removeItem(full(key))
      } catch {
        // ignore
      }
    },
  }
}

/**
 * Removes kept drafts: all of them on sign-out, or every user's but `keepUserId` when a session
 * starts (a session that simply expired never signed out).
 */
export function clearDraftStorage(keepUserId?: string) {
  try {
    const keep = keepUserId ? `${PREFIX}${keepUserId}:` : null
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key?.startsWith(PREFIX) && !(keep && key.startsWith(keep))) doomed.push(key)
    }
    for (const key of doomed) localStorage.removeItem(key)
  } catch {
    // ignore
  }
}
